// Merch shop: cart (Alpine + localStorage), embedded Stripe Checkout, and the
// post-payment return/status page. Loaded only on /shop* pages.

const CART_KEY = "merchCart";
const SHIPPING_CENTS = 1500; // display only; the real charge is set server-side

function readCart() {
  try {
    return JSON.parse(localStorage.getItem(CART_KEY) || "[]");
  } catch (e) {
    return [];
  }
}

function fmt(cents) {
  return "$" + (cents / 100).toFixed(2);
}

// Alpine cart component used by the shop grid + drawer.
window.merchShop = function merchShop() {
  return {
    open: false,
    items: readCart(),
    shipping: SHIPPING_CENTS,
    fmt,
    save() {
      localStorage.setItem(CART_KEY, JSON.stringify(this.items));
    },
    add(product) {
      const key = product.id + "|" + (product.size || "");
      const existing = this.items.find((i) => i.key === key);
      if (existing) {
        existing.qty += 1;
      } else {
        this.items.push({ ...product, key, qty: 1 });
      }
      this.save();
      this.open = true;
    },
    inc(key) {
      const item = this.items.find((i) => i.key === key);
      if (item) {
        item.qty += 1;
        this.save();
      }
    },
    dec(key) {
      const item = this.items.find((i) => i.key === key);
      if (!item) return;
      item.qty -= 1;
      if (item.qty < 1) {
        this.remove(key);
      } else {
        this.save();
      }
    },
    remove(key) {
      this.items = this.items.filter((i) => i.key !== key);
      this.save();
    },
    get count() {
      return this.items.reduce((sum, i) => sum + i.qty, 0);
    },
    get subtotal() {
      return this.items.reduce((sum, i) => sum + i.price * i.qty, 0);
    },
    get total() {
      return this.subtotal + (this.items.length ? this.shipping : 0);
    },
    checkout() {
      if (!this.items.length) return;
      window.location = "/shop/checkout";
    },
  };
};

// Embedded Checkout mount (checkout page).
async function initCheckout(el) {
  const cart = readCart();
  if (!cart.length) {
    window.location = "/shop";
    return;
  }

  const stripe = Stripe(
    import.meta.env.VITE_STRIPE_MERCH_PUB_KEY || import.meta.env.VITE_STRIPE_PUB_KEY
  );
  const csrf = el.dataset.csrf;

  const fetchClientSecret = async () => {
    const res = await fetch("/actions/pys/shop/checkout-session", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrf,
      },
      body: JSON.stringify({ cart }),
    });
    const data = await res.json();
    if (!res.ok || data.error || !data.clientSecret) {
      throw new Error(data.error || "Unable to start checkout.");
    }
    return data.clientSecret;
  };

  try {
    const checkout = await stripe.initEmbeddedCheckout({ fetchClientSecret });
    checkout.mount("#checkout");
  } catch (e) {
    el.innerHTML =
      '<div class="border border-red-200 bg-red-50 text-red-700 rounded-md p-4">' +
      (e.message || "Unable to start checkout. Please try again.") +
      ' <a href="/shop" class="underline">Return to shop</a></div>';
  }
}

// Return / status page.
async function initReturn(el) {
  const sessionId = new URLSearchParams(window.location.search).get("session_id");
  const loadingEl = el.querySelector("[data-loading]");
  const successEl = el.querySelector("[data-success]");

  if (!sessionId) {
    window.location = "/shop";
    return;
  }

  try {
    const res = await fetch(
      "/actions/pys/shop/session-status?session_id=" + encodeURIComponent(sessionId),
      { headers: { Accept: "application/json" } }
    );
    const data = await res.json();

    if (data.status === "complete") {
      localStorage.removeItem(CART_KEY);
      if (loadingEl) loadingEl.classList.add("hidden");
      if (successEl) successEl.classList.remove("hidden");
      const emailEl = el.querySelector("[data-email]");
      if (emailEl && data.customer_email) emailEl.textContent = data.customer_email;
    } else if (data.status === "open") {
      window.location = "/shop/checkout";
    } else if (loadingEl) {
      loadingEl.innerHTML =
        '<h1 class="pys-h1">We couldn\'t confirm your order</h1><p class="mt-4"><a href="/shop" class="underline">Return to shop</a></p>';
    }
  } catch (e) {
    if (loadingEl) {
      loadingEl.innerHTML =
        '<h1 class="pys-h1">We couldn\'t confirm your order</h1><p class="mt-4"><a href="/shop" class="underline">Return to shop</a></p>';
    }
  }
}

const checkoutEl = document.getElementById("checkout");
if (checkoutEl) initCheckout(checkoutEl);

const returnEl = document.getElementById("shop-return");
if (returnEl) initReturn(returnEl);

// The product grid uses Sprig (htmx), which swaps in fresh DOM on search/filter.
// Re-init Alpine on the swapped nodes so physical-product cards (variant picker,
// add-to-cart) wire up; they inherit the surrounding merchShop() scope.
document.addEventListener("htmx:afterSwap", (event) => {
  if (window.Alpine && event.target) {
    window.Alpine.initTree(event.target);
  }
});
